import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  styled,
  Switch,
  SwitchProps,
  TextField,
  Typography,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomInput } from "global/components";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { UserFields } from "models/interfaces";
import { useEffect, useState } from "react";
import EditIcon from "assets/icons1/EditIcon.svg";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import settingsStyles from "../Settings.styles";
import {
  getActiveCampaigner,
  getCampaignerById,
  updateCampaigner,
  updateCampaignerStatus,
} from "./UsersService";
import {
  fetchRoles,
  resetInActiveUserLimit,
  resetMFA,
  unlockUser,
} from "../SettingsService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import ActivateUserIcon from "assets/icons1/ActivateUserIcon.svg";
import Deactivate_Icon from "assets/icons1/Deactivate_Icon.svg";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { KeyboardBackspace } from "@mui/icons-material";
import { RouterLink } from "components/router-link";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import {
  changeIsCampaignSubmitted,
  selectIsCampaignSubmitted,
} from "redux/generalSlice";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 38,
  height: 19,
  padding: 0,
  overflow: "visible",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 1,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      "& + .MuiSwitch-track": {
        backgroundColor: "#fff",
        opacity: 1,
        border: "1px solid #5A6678",
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      backgroundColor: "#fff",
      color: "#E9E0FF",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: "#fff",
      backgroundColor: "#E9E0FF",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 19,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#FFF",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    border: "1px solid #5A6678",
  },
}));

const ViewUser = (props: any) => {
  const dispatch = useAppDispatch();
  const isCampaignSubmitted = useAppSelector(selectIsCampaignSubmitted);
  const theme = useTheme();
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const userData = props.location?.state?.userData;
  const classes = settingsStyles(theme);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiLoading, setAPILoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [fields, setFields] = useState<UserFields>({
    email: "",
    userId: 0,
    firstName: "",
    lastName: "",
    contactNo: "",
    managedBy: "",
    managerId: 0,
    roleIds: [],
    status: "",
    allowedEmailCount: 0,
    multiFactorLogin: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    email: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    managedBy: "",
    roleIds: [],
    allowedEmailCount: "",
  });
  const [managers, setManagers] = useState([]);
  const [selectedRole, setSelectedRole] = useState<string | null>("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [roles, users, campaigner] = await Promise.all([
        fetchRoles("All", 0),
        getActiveCampaigner(),
        getCampaignerById(userData?.email),
      ]);
      setRoles(
        roles.map((role: any) => {
          return {
            id: role.id,
            name: role.name,
          };
        })
      );
      setManagers(users);
      setFields({
        userId: campaigner.userId || 0,
        email: campaigner.email || "",
        managedBy: campaigner.managedBy || "",
        managerId: campaigner.managerId || 0,
        roleIds: campaigner.roleIds || [],
        allowedEmailCount: campaigner.allowedEmailCount || 0,
        status: campaigner.status || "",
        multiFactorLogin: campaigner.multiFactorLogin,
      });
      setSelectedRole(campaigner.role || "");
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const updateCampaignerHandler = async () => {
    if (handleValidate()) {
      try {
        setAPILoading(true);
        const body: any = {
          userId: fields?.userId,
          allowedEmailCount: +fields.allowedEmailCount,
          managerId: fields.managerId,
          email: fields.email,
          roleIds: fields.roleIds,
        };
        await updateCampaigner(body);
        openSuccessNotification("Campaigner updated successfully!");
        dispatch(changeIsCampaignSubmitted(!isCampaignSubmitted));
        setAPILoading(false);
      } catch (error: any) {
        setAPILoading(false);
        handleErrorMessage(error);
      }
    }
  };

  const updateCampaignerStatusHandler = async () => {
    try {
      setIsLoading(true);
      const res = await updateCampaignerStatus(
        fields.userId || 0,
        fields?.status === "Active" ? "Inactive" : "Active"
      );
      if (res && fields?.status === "Active") {
        try {
          await resetInActiveUserLimit(fields.userId!);
        } catch (error: any) {
          handleErrorMessage(error);
        }
      }
      openSuccessNotification("Campaigner status updated!");
      await fetchData();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleResetMFA = async (email: string) => {
    try {
      await resetMFA(email);
      openSuccessNotification(`MFA has been reset for ${email}.`);
      await fetchData();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleUnlockUser = async (email: string) => {
    try {
      await unlockUser(email);
      openSuccessNotification(
        `Account associated with ${email} has been unlocked successfully.`
      );
      await fetchData();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleOnChangeInputField = (e: any) => {
    const { value } = e.target;
    const spaceSeparatedValue = value.replace(/\s+/g, "");
    setFields({ ...fields, [e.target.name]: spaceSeparatedValue });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handleManagerSelect = (manager: { id: number; label: string }) => {
    setFields({
      ...fields,
      managedBy: manager?.label || "",
      managerId: manager?.id || 0,
    });
    setErrors({
      ...errors,
      managedBy: "",
    });
  };

  const handleSelectChange = (event: any) => {
    const parsedRole = JSON.parse(event?.target.value);
    setSelectedRole(parsedRole.name);
    setFields({
      ...fields,
      [event.target.name]: [parsedRole.id],
    });
  };

  const handleValidate = () => {
    let error = { ...errors };
    let isValid: boolean = true;
    if (!fields.roleIds) {
      error.roleIds = "Please select role";
      isValid = false;
    }
    if (!fields.managedBy) {
      error.managedBy = "Please select the manager email";
      isValid = false;
    }
    setErrors(error);
    return isValid;
  };

  const getPageHeader = () => {
    const isUserActive = fields?.status === "Active";
    const getStatusColors = isUserActive ? "#72C20B" : "#FF9436";
    return (
      <>
        <Stack direction="row" alignItems="center">
          <Link
            color="text.primary"
            href={viewpaths.contactsViewPath}
            sx={{
              alignItems: "center",
              display: "inline-flex",
            }}
            onClick={(e) => {
              e.preventDefault();
              history.push(viewpaths.usersViewPath);
            }}
            underline="hover"
            component={RouterLink}
          >
            <KeyboardBackspace
              htmlColor="#475569"
              sx={{ cursor: "pointer", pr: 1 }}
            />
            <Typography variant="body1" color="text.primary" fontWeight={700}>
              User Profile
            </Typography>
          </Link>
        </Stack>

        {isTruthy(fields) && (
          <Stack direction="column" pt={1}>
            <Stack
              alignItems="flex-start"
              direction={{
                xs: "column",
                md: "row",
              }}
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction={"column"} spacing={1}>
                <Stack
                  direction={{
                    xs: "column",
                    md: "row",
                  }}
                  spacing={1}
                >
                  {props.isLoading ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={25}
                      width={250}
                    />
                  ) : (
                    <>
                      <Typography
                        color="text.primary"
                        fontSize={smUp ? "1.375rem" : "1rem"}
                        fontWeight={600}
                        pl={0.5}
                      >
                        {fields?.email}
                      </Typography>
                      <Typography
                        sx={{
                          color: getStatusColors,
                          display: "flex",
                          alignItems: "center",
                        }}
                        variant="subtitle2"
                      >
                        <Box
                          sx={{
                            borderRadius: "50%",
                            height: 6,
                            width: 6,
                            mx: 0.5,
                            backgroundColor: getStatusColors,
                          }}
                        />
                        {fields?.status}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={2}>
                <Button
                  id="status_change_btn"
                  variant="text"
                  size="small"
                  startIcon={
                    <Stack>
                      <img
                        src={isUserActive ? Deactivate_Icon : ActivateUserIcon}
                        alt="Status icon"
                      />
                    </Stack>
                  }
                  disabled={isLoading}
                  onClick={() => updateCampaignerStatusHandler()}
                  sx={{
                    px: "0px !important",
                    color: fields?.status === "Active" ? "#FF9436" : "#72C20B",
                    "& .MuiButton-startIcon": {
                      marginRight: "2px",
                    },
                  }}
                >
                  {fields?.status === "Active"
                    ? "Deactivate user"
                    : "Activate User"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  const getEditUserDetails = () => {
    return (
      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            User Details
          </Typography>
          <IconButton onClick={() => setIsEdit(!isEdit)}>
            <img src={EditIcon} alt="Edit Icon" />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <CustomInput
                label="Email"
                placeHolder="Enter email"
                value={fields?.email}
                disabled={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Stack direction={"column"} spacing={1} width="100%">
              <InputLabel required sx={{ ...classes.inputLabel }}>
                Manager Email
              </InputLabel>
              <CustomAutoComplete
                textFieldVariant="outlined"
                id="managedBy"
                placeHolder="Select Manager Email"
                options={managers.map((manager: any) => ({
                  label: manager.email,
                  id: manager.userId,
                }))}
                value={fields.managedBy}
                onChange={(event: any, newValue: any) => {
                  handleManagerSelect(newValue);
                }}
                disabled={!isEdit}
                errorMessage={errors?.managedBy}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Stack direction={"column"} spacing={2} width="100%">
              <InputLabel required sx={{ ...classes.inputLabel }}>
                Role
              </InputLabel>
              <Select
                required
                fullWidth
                sx={classes.select}
                labelId="role-id"
                id="role-id"
                name="roleIds"
                value={selectedRole || ""}
                onChange={handleSelectChange}
                disabled={!isEdit}
                renderValue={(selected) =>
                  selected ? (
                    <Typography variant="body2" fontWeight={600}>
                      {selected}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="#bbbfc6">
                      Select Role
                    </Typography>
                  )
                }
                displayEmpty
                MenuProps={MenuProps}
                error={
                  !isTruthy(fields?.roleIds[0]) && errors?.roleIds.length !== 0
                }
              >
                {roles.map((role: any) => (
                  <MenuItem
                    key={role.name}
                    value={JSON.stringify({ id: role.id, name: role.name })}
                    sx={classes.dropDownMenuItem}
                  >
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Email Counts
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Stack display="flex" flexDirection={"column"} spacing={1.5}>
                <Typography
                  sx={{
                    color: "#475569",
                    fontSize: "14px",
                    flexShrink: 0,
                    fontWeight: 400,
                    mr: 2,
                    minWidth: "110px",
                  }}
                >
                  Provided Email Count
                </Typography>
                <Divider
                  sx={{
                    borderColor: "rgba(71, 85, 105, 0.25)",
                  }}
                />
                <TextField
                  variant="standard"
                  id="allowedEmailCount"
                  type="number"
                  name="allowedEmailCount"
                  placeholder="Enter provided email count"
                  value={fields?.allowedEmailCount}
                  onChange={(event: any) => {
                    event.target.value = Math.abs(event.target.value);
                    handleOnChangeInputField(event);
                  }}
                  fullWidth
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "22px",
                      color: theme.palette.primary.main,
                    },
                  }}
                  disabled={!isEdit}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack display="flex" flexDirection={"column"} spacing={1.5}>
                <Typography
                  sx={{
                    color: "#475569",
                    fontSize: "14px",
                    flexShrink: 0,
                    fontWeight: 400,
                  }}
                >
                  Used Emails
                </Typography>
                <Divider
                  sx={{
                    borderColor: "rgba(71, 85, 105, 0.25)",
                  }}
                />

                <Typography
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: "22px",
                    fontWeight: 600,
                  }}
                >
                  {userData?.emailCount || "0"}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Stack
          display="flex"
          justifyContent="end"
          alignItems="end"
          width="100%"
        >
          <Button
            id="save_btn"
            variant="contained"
            disabled={isLoading || !isEdit}
            onClick={() => updateCampaignerHandler()}
          >
            Update Profile
          </Button>
        </Stack>
      </Card>
    );
  };

  const getMFAandStatusSection = () => {
    return (
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} lg={6} xl={6}>
          <Stack spacing={2}>
            <Card sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    2MFA
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#6B7280",
                    }}
                  >
                    Secures your account with two verification steps.
                  </Typography>
                </Box>
                <CustomSwitch
                  sx={{ m: 1 }}
                  defaultChecked={fields?.multiFactorLogin}
                  onChange={() => handleResetMFA(fields?.email)}
                  disabled={!fields?.multiFactorLogin}
                />
              </Box>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={6} xl={6}>
          <Stack spacing={2}>
            <Card sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    Profile Lock
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#6B7280",
                    }}
                  >
                    Allows controlled access to your profile enhancing privacy
                    and security.
                  </Typography>
                </Box>
                <CustomSwitch
                  sx={{ m: 1 }}
                  onChange={() => handleUnlockUser(fields?.email)}
                  checked={fields?.status === "Locked"}
                  disabled={!(fields?.status === "Locked")}
                />
              </Box>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const getHeaderLoader = () => {
    return <Skeleton animation="wave" variant="text" height={50} />;
  };

  const getEditProfileLoader = () => {
    return (
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Skeleton animation="wave" variant="rounded" height={250} />
        </Grid>
      </Grid>
    );
  };

  const getMFAandStatusSectionLoader = () => {
    return (
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} lg={6} xl={6}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Grid>
        <Grid item xs={12} lg={6} xl={6}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Grid>
      </Grid>
    );
  };

  const getViewUserPageBody = () => {
    return (
      <Stack direction="column" spacing={1}>
        {isLoading ? getHeaderLoader() : getPageHeader()}
        <Box>{isLoading ? getEditProfileLoader() : getEditUserDetails()}</Box>
      </Stack>
    );
  };
  return (
    <>
      <CenteredAuthenticatedLayout>
        {getViewUserPageBody()}
        {isLoading ? getMFAandStatusSectionLoader() : getMFAandStatusSection()}
        <CustomLoader isLoading={apiLoading} />
      </CenteredAuthenticatedLayout>
    </>
  );
};

export default ViewUser;
